import { TreeNodeData, TransformedTreeNode } from '../types';

/**
 * Formats a number for display - rounds to 2 decimal places
 * and removes trailing zeros
 */
function formatValue(value: number): string {
  const rounded = Math.round(value);
  return rounded.toString();
}

/**
 * Recursively transforms tree data into the format required by Ant Design Table
 * @param node - The current tree node to transform
 * @param parentKey - The parent node's key for building unique keys
 * @param level - Current depth level in the tree
 * @param expandedKeys - Set to collect keys that should be expanded by default
 * @returns Transformed tree node with all required properties
 */
function transformNode(
  node: TreeNodeData,
  parentKey: string,
  level: number,
  expandedKeys: Set<string>
): TransformedTreeNode {
  const key = parentKey;
  const hasChildren = Boolean(node.children && node.children.length > 0);
  const isSelectable = node.value !== undefined;

  // Add to expanded keys if it's the root or first level
  if (level <= 1) {
    expandedKeys.add(key);
  }

  const transformed: TransformedTreeNode = {
    key,
    name: node.name,
    metadata: node.metadata,
    value: node.value,
    selected: node.selected ?? false,
    level,
    hasChildren,
    isSelectable,
    displayValue: node.value !== undefined ? formatValue(node.value) : undefined,
    rawValue: node.value,
  };

  // Recursively transform children
  if (node.children) {
    transformed.children = node.children.map((child) => {
      // Generate name-based key using URL encoding to prevent collisions
      const encodedName = encodeURIComponent(child.name);
      const childKey = key ? `${key}:${encodedName}` : encodedName;
      return transformNode(child, childKey, level + 1, expandedKeys);
    });
  }

  return transformed;
}

/**
 * Transforms the root tree data structure
 * @param data - The tree data with root node
 * @returns Transformed tree node and default expanded keys
 */
export function transformTreeData(data: TreeNodeData): {
  transformedData: TransformedTreeNode;
  defaultExpandedKeys: string[];
} {
  const expandedKeys = new Set<string>();
  // Generate root key using URL encoding to prevent collisions
  const encodedName = encodeURIComponent(data.name);
  const transformedData = transformNode(data, encodedName, 0, expandedKeys);

  return {
    transformedData,
    defaultExpandedKeys: Array.from(expandedKeys),
  };
}

/**
 * Flattens tree structure into array while maintaining hierarchy
 * Used for virtual scrolling if needed
 */
export function flattenTree(node: TransformedTreeNode, result: TransformedTreeNode[] = []): TransformedTreeNode[] {
  result.push(node);
  if (node.children) {
    node.children.forEach(child => flattenTree(child, result));
  }
  return result;
}

/**
 * Checks if a node or any of its descendants are selected
 * @param node - The tree node to check
 * @param selectedKeys - Set of selected node keys
 * @returns True if node or any descendant is selected
 */
function hasSelectedDescendant(node: TransformedTreeNode, selectedKeys: Set<string>): boolean {
  if (selectedKeys.has(node.key)) {
    return true;
  }
  
  if (node.children) {
    return node.children.some(child => hasSelectedDescendant(child, selectedKeys));
  }
  
  return false;
}

/**
 * Filters tree to show only selected nodes and their parent paths
 * @param node - The tree node to filter
 * @param selectedKeys - Set of selected node keys
 * @returns Filtered tree node or null if should be excluded
 */
function filterSelectedNodes(
  node: TransformedTreeNode,
  selectedKeys: Set<string>
): TransformedTreeNode | null {
  // Check if this node or any descendant is selected
  if (!hasSelectedDescendant(node, selectedKeys)) {
    return null;
  }

  // Clone the node
  const filtered: TransformedTreeNode = {
    ...node,
  };

  // Recursively filter children
  if (node.children) {
    const filteredChildren = node.children
      .map(child => filterSelectedNodes(child, selectedKeys))
      .filter((child): child is TransformedTreeNode => child !== null);
    
    if (filteredChildren.length > 0) {
      filtered.children = filteredChildren;
    } else {
      // No children after filtering, remove children property
      delete filtered.children;
    }
  }

  return filtered;
}

/**
 * Filters tree data to show only selected nodes and their parents
 * Returns null if no nodes are selected
 * @param node - The root tree node
 * @param selectedKeys - Set of selected node keys
 * @returns Filtered tree with selected nodes and parents, or null if empty
 */
export function filterTreeToSelected(
  node: TransformedTreeNode,
  selectedKeys: Set<string>
): TransformedTreeNode | null {
  if (selectedKeys.size === 0) {
    return null;
  }
  
  return filterSelectedNodes(node, selectedKeys);
}

/**
 * Collects all keys from a tree node and its descendants
 * Used to auto-expand filtered tree
 * @param node - The tree node
 * @param keys - Set to collect keys into
 */
export function collectAllKeys(node: TransformedTreeNode, keys: Set<string> = new Set()): Set<string> {
  keys.add(node.key);
  if (node.children) {
    node.children.forEach(child => collectAllKeys(child, keys));
  }
  return keys;
}

/**
 * Checks if a node or any of its descendants match the search term
 * @param node - The tree node to check
 * @param searchTerm - The search term (case-insensitive)
 * @returns True if node or any descendant matches
 */
function hasMatchingDescendant(node: TransformedTreeNode, searchTerm: string): boolean {
  const lowerSearchTerm = searchTerm.toLowerCase();
  
  // Check if current node matches
  if (node.name.toLowerCase().includes(lowerSearchTerm)) {
    return true;
  }
  
  // Check children
  if (node.children) {
    return node.children.some(child => hasMatchingDescendant(child, lowerSearchTerm));
  }
  
  return false;
}

/**
 * Filters tree to show only nodes matching search term and their parents
 * @param node - The tree node to filter
 * @param searchTerm - The search term (case-insensitive)
 * @returns Filtered tree node or null if should be excluded
 */
function filterSearchNodes(
  node: TransformedTreeNode,
  searchTerm: string
): TransformedTreeNode | null {
  // Check if this node or any descendant matches
  if (!hasMatchingDescendant(node, searchTerm)) {
    return null;
  }

  // Clone the node
  const filtered: TransformedTreeNode = {
    ...node,
  };

  // Recursively filter children
  if (node.children) {
    const filteredChildren = node.children
      .map(child => filterSearchNodes(child, searchTerm))
      .filter((child): child is TransformedTreeNode => child !== null);
    
    if (filteredChildren.length > 0) {
      filtered.children = filteredChildren;
    } else {
      // No children after filtering, remove children property
      delete filtered.children;
    }
  }

  return filtered;
}

/**
 * Filters tree data to show only nodes matching search term
 * Returns null if no matches found
 * @param node - The root tree node
 * @param searchTerm - The search term to filter by
 * @returns Filtered tree with matching nodes and parents, or null if no matches
 */
export function filterTreeBySearch(
  node: TransformedTreeNode,
  searchTerm: string
): TransformedTreeNode | null {
  if (!searchTerm || searchTerm.trim() === '') {
    return node;
  }
  
  return filterSearchNodes(node, searchTerm.trim());
}
